import { chunk } from 'lodash'
import { type WorkflowsKey } from './queryHelpers'
import { readWorkflows } from './read'
import { type EntityWorkflowsDocument, type WorkflowEntityType } from './types'

// One filter per request: the server ANDs filters, and a paired block's
// context has both ids, so sending both would drop every other id.
const filterFor = (entityType: WorkflowEntityType, entityIds: string[]) =>
  entityType === 'block'
    ? { 'context.block_id': entityIds }
    : { 'context.moment_id': entityIds }

export const entityWorkflowsIdentifiers = (
  workspaceUuid: string,
  entityType: WorkflowEntityType,
  entityIds: string[]
): WorkflowsKey => ({
  workspaceUuid,
  queryParams: { filter: filterFor(entityType, entityIds) },
})

// Keeps each request line under the server's length limit; the server has no
// limit on the number of ids. It is also the page limit: a block or moment has
// at most one workflow (unique indexes `contexts_block_id_key` and
// `contexts_moment_id_key`), so one page holds every result.
const idsPerRequest = 100

/**
 * Reads the workflows of the given blocks or moments (by UUID), with their
 * contexts and current stages, in requests of up to 100 ids each.
 */
export const readEntityWorkflows = async (
  entityType: WorkflowEntityType,
  entityIds: string[]
): Promise<EntityWorkflowsDocument> => {
  const results = await Promise.all(
    chunk(entityIds, idsPerRequest).map((chunkedIds) =>
      readWorkflows({
        params: {
          query: {
            filter: filterFor(entityType, chunkedIds),
            include: ['context', 'current_stage'],
            page: { limit: idsPerRequest },
          },
        },
      })
    )
  )

  return {
    data: results.flatMap((document) => document.data),
    included: results.flatMap((document) => document.included ?? []),
  }
}
